import { Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { BLUR_ALL, BLUR_LAST, TRACK_EVENTS } from "core/consts";
import { EVENTS } from "core/consts/events";
import { useDateLocale } from "core/hooks/useDateLocale";
import { isAppEmbedded } from "core/model/utils/featureFlags";
import { getKey } from "core/model/utils/strings";
import {
  Event,
  Filev1,
  IconType,
  LastSeen,
  QueryProgress,
  SearchType,
} from "core/types";
import { formatRelative, fromUnixTime } from "date-fns";
import { IconCircle } from "ds_legacy/components/Chip";
import LinkComponent from "ds_legacy/components/Link";
import { CARD_BORDER, GREY_300, GREY_600 } from "ds_legacy/materials/colors";
import { HorizontalLayout, VerticalLayout } from "ds_legacy/materials/layouts";
import { border, dp, margin, padding } from "ds_legacy/materials/metrics";
import {
  Body,
  Caption,
  FONT_SIZE_12,
  FONT_SIZE_14,
  FONT_SIZE_20,
  FONT_WEIGHT_SEMI_BOLD,
  LINE_HEIGHT_16,
  LINE_HEIGHT_20,
  LINE_HEIGHT_24,
} from "ds_legacy/materials/typography";
import { EventSpecMessage, EventSpecs } from "dsl/atoms/EventSpecs";
import { SeenTag } from "dsl/atoms/SeenTag";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { EventMessage } from "dsl/molecules/Messenger/EventCard/EventMessagingBubble";
import { EventMessagingBubbleV2 } from "dsl/molecules/Messenger/EventCard/EventMessagingBubbleV2";
import { PrintableEventCard } from "dsl/molecules/Messenger/PrintMessenger";
import {
  BANNER_WIDTH,
  CancelButton,
  DownloadButton,
} from "dsl/organisms/Banners";
import {
  CheckIcon,
  ChevronRightIcon,
  MessageCircleIcon,
  RotateCwIcon,
  XIcon,
} from "lucide-react";
import { CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { useTracking } from "react-tracking";
import styled from "styled-components";
import { useTranslations } from "translations";

export type CardFormat = {
  blurType?: number;
  lastSeens?: LastSeen[] | null | undefined;
  seen?: boolean | null | undefined;
  seenTimestamp?: number | null | undefined;
  variant?: EventSpecs["variant"];
};

export type EventCardProps = {
  Icon?: IconType | null;
  cancelAction: (() => void) | null;
  cardFormat: CardFormat;
  eventMessages?: EventSpecMessage[];
  file?: Filev1 | null;
  isDividerVisible: boolean;
  isEventUserInitiated?: boolean;
  messages: string[] | null;
  onSubmitQueryProgress?: QueryProgress;
  patientId?: string;
  print?: boolean;
  searchType?: SearchType | null;
  sentByLabel?: string | null;
  shouldShowBefore: boolean;
  statusHeader?: string;
  testId?: string;
  timestamp?: number | null;
  type: Event["type"] | undefined;
};

type TitleLayerProps = Pick<
  EventCardProps,
  "cardFormat" | "sentByLabel" | "statusHeader" | "timestamp"
> & {
  right: boolean | undefined;
};

export const DEFAULT_ICON = MessageCircleIcon;
export const SUCCESS_ICON = CheckIcon;
export const ERROR_ICON = XIcon;
export const PENDING_ICON = RotateCwIcon;

export const TitleContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => !["right"].includes(prop),
})<{
  right?: boolean;
}>`
  box-sizing: border-box;
  display: flex;
  flex-grow: 1;
  justify-content: ${({ right }) => (right ? "flex-end" : "flex-start")};
`;

const EventCardContainer = styled.li.withConfig({
  shouldForwardProp: (prop) =>
    !["isDividerVisible", "shouldShowBefore"].includes(prop),
})<{
  isDividerVisible: boolean;
  shouldShowBefore: boolean;
}>`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  margin: ${margin(0, 2)};

  ${({ isDividerVisible, shouldShowBefore }) =>
    shouldShowBefore &&
    `&::before {
      content: "";
      width: ${dp(0)};
      height: ${dp(isDividerVisible ? 48 : 16)};
      border-left: ${dp(1)} solid;
      border-color: ${isDividerVisible ? GREY_300 : "transparent"};
      position: relative;
      left: 50%;
      transform: translateX(-50%);
      z-index: 0;
    }`}
`;

const VerticalContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => !["order", "right"].includes(prop),
})<{
  order: CSSProperties["order"];
  right?: boolean;
}>`
  align-items: ${({ right }) => (right ? "flex-end" : "flex-start")};
  display: flex;
  flex-direction: column;
  order: ${({ order }) => order};
  width: 84%;
`;

const SeenFooter = styled(Caption).withConfig({
  shouldForwardProp: (prop) => !["alignItems"].includes(prop),
})<{
  alignItems: CSSProperties["alignItems"];
}>`
  display: flex;
  flex-direction: column;
  align-items: ${({ alignItems }) => alignItems ?? "flex-end"};
  margin: 0;
`;

const SpaceY = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    !["margin", "marginChildren", "right"].includes(prop),
})<{
  margin?: CSSProperties["margin"];
  marginChildren?: CSSProperties["margin"];
  right: boolean;
}>`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin: ${({ margin: m }) => m};

  & * + * {
    margin: ${({ marginChildren: m }) => m || margin(0.5, 0, 0, 0)};
  }
`;

const getBlur = (blurType: number | undefined, isLast: boolean) => {
  switch (blurType) {
    case BLUR_ALL:
      return true;
    case BLUR_LAST:
      return isLast;
    default:
      return false;
  }
};

function IconItem({
  Icon,
  style,
  variant,
}: {
  Icon: IconType;
  style?: CSSProperties;
  variant?: EventSpecs["variant"];
}) {
  const theme = useTheme();

  return (
    <VerticalLayout style={style}>
      <IconCircle
        backgroundColor={theme.palette.grey["50"]}
        border={border({ color: CARD_BORDER })}
      >
        <Icon
          size={FONT_SIZE_20}
          style={{
            color:
              theme.palette[variant === "error" ? variant : "primary"].main,
            width: dp(20),
          }}
        />
      </IconCircle>
    </VerticalLayout>
  );
}

const TextHack = styled.div<{
  right?: boolean;
}>`
  display: flex;
  justify-content: ${({ right }) => (right ? "flex-end" : "flex-start")};
  width: 100%;
`;

function TitleLayer({
  cardFormat,
  right,
  sentByLabel,
  statusHeader,
  timestamp,
}: TitleLayerProps) {
  const { variant } = cardFormat;
  const { currentLocale: locale } = useDateLocale();

  return (
    <TitleContainer data-testid="event_card_header" right={right}>
      <VerticalContainer order={1} right={right}>
        <HorizontalLayout aligned>
          {!right && (
            <>
              {sentByLabel && (
                <>
                  <Typography
                    color={GREY_600}
                    fontSize={FONT_SIZE_12}
                    lineHeight={LINE_HEIGHT_16}
                  >
                    {sentByLabel}
                  </Typography>
                </>
              )}
              {sentByLabel && timestamp && (
                <span
                  style={{
                    color: GREY_600,
                    display: "flex",
                    fontSize: FONT_SIZE_12,
                  }}
                >
                  &nbsp; &#x2022; &nbsp;
                </span>
              )}
            </>
          )}
          {timestamp && (
            <Typography
              color={GREY_600}
              fontSize={FONT_SIZE_12}
              lineHeight={LINE_HEIGHT_16}
            >
              {formatRelative(fromUnixTime(timestamp), new Date(), {
                locale,
              })}
            </Typography>
          )}
        </HorizontalLayout>
        {statusHeader && (
          <TextHack right={right}>
            <Body
              as="p"
              textAlign={right ? "right" : "left"}
              {...(variant ? { [variant]: true } : {})}
              color={GREY_600}
              fontWeight={FONT_WEIGHT_SEMI_BOLD}
              margin={margin(0)}
              whiteSpace="pre-wrap"
            >
              {statusHeader}
            </Body>
          </TextHack>
        )}
      </VerticalContainer>
    </TitleContainer>
  );
}

export function EventCardMessage({
  cancelMutationProgress,
  cardFormat,
  eventMessages,
  file,
  handleCancel,
  Icon,
  isDividerVisible,
  isEventUserInitiated,
  messages,
  patientId,
  searchType,
  shouldShowBefore,
  statusHeader,
  testId,
  timestamp,
}: Pick<
  EventCardProps,
  | "shouldShowBefore"
  | "isDividerVisible"
  | "cardFormat"
  | "eventMessages"
  | "file"
  | "Icon"
  | "isEventUserInitiated"
  | "messages"
  | "searchType"
  | "statusHeader"
  | "timestamp"
  | "patientId"
> & {
  cancelMutationProgress: QueryProgress | undefined;
  handleCancel: (() => void) | null;
  testId?: string;
}) {
  const { trackEvent } = useTracking();
  const navigate = useNavigate();
  const { currentLocale: locale } = useDateLocale();
  const { blurType } = cardFormat;
  const formattedTimestamp = timestamp
    ? formatRelative(fromUnixTime(timestamp), new Date(), { locale })
    : null;

  return (
    <EventCardContainer
      data-testid={testId}
      isDividerVisible={isDividerVisible}
      shouldShowBefore={shouldShowBefore}
    >
      <HorizontalLayout
        data-testid="event_card_header"
        justify="center"
        overflow="visible"
        width="100%"
        zIndex="1"
      >
        <VerticalLayout
          aligned
          border={border({ color: CARD_BORDER })}
          boxSizing="border-box"
          maxWidth={dp(BANNER_WIDTH)}
          overflow="visible"
          padding={padding(Icon ? 0 : 1, 2, 1, 2)}
          width="85%"
        >
          {Icon && (
            <IconItem
              Icon={Icon}
              style={{ marginTop: dp(-20) }}
              variant={cardFormat.variant}
            />
          )}
          <VerticalLayout aligned padding={padding(1)}>
            <div>
              {formattedTimestamp && (
                <Typography
                  color={GREY_600}
                  fontSize={FONT_SIZE_12}
                  lineHeight={LINE_HEIGHT_16}
                >
                  {formattedTimestamp}
                </Typography>
              )}
            </div>
            <HorizontalLayout>
              {statusHeader && (
                <Typography
                  fontSize={FONT_SIZE_14}
                  lineHeight={LINE_HEIGHT_24}
                  sx={{
                    color:
                      cardFormat.variant === "success"
                        ? "primary.main"
                        : "common.black",
                  }}
                  textAlign="center"
                  whiteSpace="normal"
                >
                  {statusHeader}
                </Typography>
              )}
            </HorizontalLayout>
            {eventMessages?.map(
              ({ date, link, reasons, time, title }, index) => (
                <VerticalLayout
                  key={getKey(index)}
                  aligned
                  boxSizing="border-box"
                  margin={margin(1, 0, 0, 0)}
                  width="100%"
                >
                  <Typography
                    fontSize={FONT_SIZE_14}
                    lineHeight={LINE_HEIGHT_20}
                    sx={{ color: "grey.700", wordBreak: "break-word" }}
                    textAlign="center"
                  >
                    {title}
                  </Typography>
                  <Typography
                    fontSize={FONT_SIZE_14}
                    lineHeight={LINE_HEIGHT_20}
                    sx={{ color: "grey.700", wordBreak: "break-word" }}
                    textAlign="center"
                  >
                    {date}
                  </Typography>
                  <Typography
                    fontSize={FONT_SIZE_14}
                    lineHeight={LINE_HEIGHT_20}
                    sx={{ color: "grey.700", wordBreak: "break-word" }}
                    textAlign="center"
                  >
                    {time}
                  </Typography>
                  {reasons?.map((reason, i) => {
                    return (
                      <Typography
                        key={getKey(reason, i)}
                        fontSize={FONT_SIZE_14}
                        lineHeight={LINE_HEIGHT_20}
                        sx={{
                          color: "grey.700",
                          wordBreak: "break-word",
                        }}
                        textAlign="center"
                      >
                        {reason}
                      </Typography>
                    );
                  })}
                  {file && (
                    <DownloadButton file={file} right={isEventUserInitiated} />
                  )}
                  {link && (
                    <LinkComponent
                      EndIcon={ChevronRightIcon}
                      fontSize={FONT_SIZE_14}
                      value={link.label}
                      onAction={() => {
                        trackEvent({
                          name: TRACK_EVENTS.MESSENGER_SEE_DOCUMENT_LINK,
                        });
                        navigate(link.to);
                      }}
                    />
                  )}
                </VerticalLayout>
              ),
            )}
            <HorizontalLayout>
              {!isAppEmbedded() && handleCancel && (
                <CancelButton
                  withButton
                  onCancel={handleCancel}
                  onSubmitQueryProgress={cancelMutationProgress}
                />
              )}
            </HorizontalLayout>
          </VerticalLayout>
        </VerticalLayout>
      </HorizontalLayout>
      <HorizontalLayout
        boxSizing="border-box"
        margin={margin(0, 0, 0, 0)}
        overflow="visible"
        width="100%"
      >
        <SpaceY
          margin={messages?.length ? margin(1, 0, 0, 0) : "0"}
          right={!!isEventUserInitiated}
        >
          {messages?.map((message, index) => (
            <EventMessagingBubbleV2
              key={index.toString()}
              blur={getBlur(blurType, index === messages?.length - 1)}
              isMyMessage={!!isEventUserInitiated}
              message={message}
            />
          ))}
          {messages && isEventUserInitiated && (
            <SeenFooter alignItems="flex-end">
              <SeenTag
                lastSeens={cardFormat.lastSeens}
                patientId={patientId}
                searchType={searchType}
              />
            </SeenFooter>
          )}
        </SpaceY>
      </HorizontalLayout>
    </EventCardContainer>
  );
}

export default function EventCard({
  cancelAction,
  cardFormat,
  eventMessages,
  file,
  Icon,
  isDividerVisible,
  isEventUserInitiated,
  messages,
  onSubmitQueryProgress,
  patientId,
  print,
  searchType,
  sentByLabel,
  shouldShowBefore,
  statusHeader,
  testId,
  timestamp,
  type,
}: EventCardProps) {
  const toast = useToast();
  const translations = useTranslations();
  const { blurType, lastSeens } = cardFormat;

  async function handleCancel() {
    if (!cancelAction) return;

    try {
      await cancelAction();
      toast({
        color: "warning",
        message: translations.actions.undoActionCompletion,
      });
    } catch (err) {
      console.error(err);
      toast({
        color: "danger",
        message: translations.auctionRequest.uploadError,
      });
    }
  }

  if (print) {
    return (
      <PrintableEventCard
        eventMessages={eventMessages}
        messagesToDisplay={messages}
        statusHeader={statusHeader}
        timestamp={timestamp}
      />
    );
  }

  if (type !== EVENTS.ENCRYPTED_MESSAGE) {
    return (
      <EventCardMessage
        cancelMutationProgress={onSubmitQueryProgress}
        cardFormat={cardFormat}
        eventMessages={eventMessages}
        file={file}
        handleCancel={cancelAction ? handleCancel : null}
        Icon={Icon}
        isDividerVisible={isDividerVisible}
        isEventUserInitiated={isEventUserInitiated}
        messages={messages}
        patientId={patientId}
        searchType={searchType}
        shouldShowBefore={shouldShowBefore}
        statusHeader={statusHeader}
        testId={testId}
        timestamp={timestamp}
      />
    );
  }

  return (
    <EventCardContainer
      isDividerVisible={isDividerVisible}
      shouldShowBefore={shouldShowBefore}
    >
      <TitleLayer
        cardFormat={cardFormat}
        right={isEventUserInitiated}
        sentByLabel={sentByLabel}
        statusHeader={statusHeader}
        timestamp={timestamp}
      />
      <div className="relative mt-2 flex min-h-[5px] flex-grow flex-col justify-start overflow-hidden pb-4">
        <EventMessage
          eventMessages={eventMessages}
          right={isEventUserInitiated}
        />
        {messages?.map((message, index) => (
          <EventMessagingBubbleV2
            key={index.toString()}
            blur={getBlur(blurType, index === messages?.length - 1)}
            isMyMessage={!!isEventUserInitiated}
            message={message}
          />
        ))}
        {messages && isEventUserInitiated && (
          <SeenFooter alignItems="flex-end">
            <SeenTag
              lastSeens={lastSeens}
              patientId={patientId}
              searchType={searchType}
            />
          </SeenFooter>
        )}
        {file && <DownloadButton file={file} right={isEventUserInitiated} />}
        {!isAppEmbedded() && cancelAction && (
          <CancelButton
            withButton
            onCancel={handleCancel}
            onSubmitQueryProgress={onSubmitQueryProgress}
          />
        )}
      </div>
    </EventCardContainer>
  );
}
